import { useEffect, useRef, useState } from 'react';
import {
  getInstagramInsights,
  getInstagramPosts,
  publishInstagramPost,
  replyToInstagramComment,
  uploadInstagramMedia,
  getInstagramMediaLibrary,
  InstagramLibraryMedia,
  InstagramInsight,
  InstagramCarouselItem,
  InstagramPost,
  InstagramProfileStats,
  InstagramPublishRequest,
  InstagramPublishType,
  InstagramScheduledPost,
  getScheduledInstagramPosts,
  scheduleInstagramPost,
  updateScheduledInstagramPost,
  cancelScheduledInstagramPost,
} from '../../../api/instagram.service';

type Screen = 'comments' | 'publish' | 'insights';

function formatInsightValue(value: unknown) {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default function InstagramTools({
  screen,
  businessIgId,
}: {
  screen: Screen;
  businessIgId: string;
}) {
  if (screen === 'comments') return <CommentsScreen businessIgId={businessIgId} />;
  if (screen === 'publish') return <PublishScreen businessIgId={businessIgId} />;
  return <InsightsScreen businessIgId={businessIgId} />;
}

function commentsOnce(comments: InstagramPost['comments']) {
  const replyIds = new Set<string>();
  for (const comment of comments) {
    for (const reply of comment.replies || []) {
      if (reply.id) replyIds.add(reply.id);
    }
  }
  const replyTexts = new Set(
    comments.flatMap((comment) =>
      (comment.replies || []).map((reply) => reply.text.trim()).filter(Boolean)
    )
  );
  const nameForText = new Map<string, string>();
  for (const comment of comments) {
    const text = comment.text.trim();
    if (text && replyTexts.has(text) && comment.username) {
      nameForText.set(text, comment.username.replace(/^@/, ''));
    }
  }
  return comments
    .filter((comment) => {
      if (comment.id && replyIds.has(comment.id)) return false;
      const text = comment.text.trim();
      return !text || !replyTexts.has(text);
    })
    .map((comment) => ({
      ...comment,
      replies: (comment.replies || []).map((reply) => ({
        ...reply,
        username: reply.username || nameForText.get(reply.text.trim()) || '',
      })),
    }));
}

function PostsList({ businessIgId, refreshKey = 0 }: { businessIgId: string; refreshKey?: number }) {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!businessIgId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getInstagramPosts(businessIgId)
      .then((result) => {
        if (cancelled) return;
        if (!result.success) {
          setPosts([]);
          setError(result.error || 'Could not load Instagram posts.');
          return;
        }
        setPosts(result.posts);
      })
      .catch(() => {
        if (cancelled) return;
        setPosts([]);
        setError('Could not load Instagram posts.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [businessIgId, refreshKey, reloadKey]);

  const sendAsBusiness = async (payload: { commentId?: string; mediaId?: string; message: string }) => {
    const message = payload.message.trim();
    if (!message) return;
    setSending(true);
    setError(null);
    try {
      const result = await replyToInstagramComment({
        message,
        commentId: payload.commentId,
        mediaId: payload.mediaId,
        businessIgId,
      });
      if (!result?.success) {
        setError(result?.error || 'Could not post as the business.');
        return;
      }
      setDraft('');
      setReplyFor(null);
      if (payload.mediaId) {
        setCommentDrafts((current) => ({ ...current, [payload.mediaId as string]: '' }));
      }
      setReloadKey((value) => value + 1);
    } catch {
      setError('Could not post as the business.');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading posts...</p>;
  if (posts.length === 0 && error) return <p className="text-sm text-red-600">{error}</p>;
  if (posts.length === 0) return <p className="text-sm text-gray-500">No Instagram posts yet.</p>;

  return (
    <div className="space-y-4">
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {posts.map((post) => (
        <article key={post.id} className="overflow-hidden rounded-xl border border-gray-100">
          {post.imageUrl ? (
            <div className="relative">
              <img src={post.imageUrl} alt="" className="max-h-80 w-full object-cover" />
              {post.mediaType && post.mediaType !== 'IMAGE' ? (
                <span className="absolute left-3 top-3 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white">
                  {post.mediaType === 'CAROUSEL_ALBUM' ? 'Carousel' : 'Video'}
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-gray-900">
                {post.likeCount ?? '—'} likes
              </p>
              <p className="text-xs text-gray-500">
                {post.timestamp ? new Date(post.timestamp).toLocaleString() : ''}
              </p>
            </div>
            {post.caption ? <p className="mt-2 text-sm text-gray-800">{post.caption}</p> : null}
            <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
              {commentsOnce(post.comments).map((comment) => (
                <div key={comment.id}>
                  <p className="text-sm text-gray-800">
                    <span className="font-semibold">@{comment.username || 'instagram'}</span> {comment.text}
                  </p>
                  {(comment.replies || []).length > 0 ? (
                    <div className="mt-2 space-y-2 border-l-2 border-gray-200 pl-3">
                      {(comment.replies || []).map((reply) => (
                        <p key={reply.id} className="text-sm text-gray-700">
                          {reply.username ? <span className="font-semibold">@{reply.username} </span> : null}
                          {reply.text}
                        </p>
                      ))}
                    </div>
                  ) : null}
                  {replyFor === comment.id ? (
                    <form
                      className="mt-2 flex items-end gap-2 pl-3"
                      onSubmit={(event) => {
                        event.preventDefault();
                        sendAsBusiness({ commentId: comment.id, message: draft });
                      }}
                    >
                      <textarea
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        rows={2}
                        placeholder="Reply as the business"
                        className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                      <button
                        type="submit"
                        disabled={sending || !draft.trim()}
                        className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                      >
                        {sending ? 'Sending...' : 'Reply'}
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setReplyFor(comment.id);
                        setDraft('');
                      }}
                      className="mt-1 text-xs font-medium text-gray-500"
                    >
                      Reply
                    </button>
                  )}
                </div>
              ))}
              <form
                className="flex items-end gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  sendAsBusiness({ mediaId: post.id, message: commentDrafts[post.id] || '' });
                }}
              >
                <textarea
                  value={commentDrafts[post.id] || ''}
                  onChange={(event) =>
                    setCommentDrafts((current) => ({ ...current, [post.id]: event.target.value }))
                  }
                  rows={2}
                  placeholder="Comment as the business"
                  className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
                <button
                  type="submit"
                  disabled={sending || !(commentDrafts[post.id] || '').trim()}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {sending ? 'Sending...' : 'Comment'}
                </button>
              </form>
            </div>
            {post.permalink ? (
              <a
                href={post.permalink}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-block text-sm font-medium text-blue-600"
              >
                Open on Instagram
              </a>
            ) : null}
          </div>
        </article>
      ))}
    </div>
  );
}

function CommentsScreen({ businessIgId }: { businessIgId: string }) {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Instagram Comments</h2>
      <p className="mb-4 text-sm text-gray-500">
        Comments sit under each post. Reply, or add a new comment, as the business account.
      </p>
      <PostsList businessIgId={businessIgId} />
    </div>
  );
}

/** Grid of files uploaded earlier for this account, newest first. */
function MediaLibraryPicker({
  kinds,
  onSelect,
  onClose,
}: {
  kinds: ('IMAGE' | 'VIDEO')[];
  onSelect: (media: InstagramLibraryMedia) => void;
  onClose: () => void;
}) {
  const [media, setMedia] = useState<InstagramLibraryMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    getInstagramMediaLibrary()
      .then((list) => active && setMedia(list))
      .catch(() => active && setError('Could not load your uploads.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const visible = media.filter((item) => kinds.includes(item.kind));

  return (
    <div className="mt-2 rounded-lg border border-gray-200 p-3 font-normal">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium text-gray-700">Your uploads</p>
        <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700">
          Close
        </button>
      </div>
      {loading ? <p className="text-xs text-gray-500">Loading...</p> : null}
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
      {!loading && !error && visible.length === 0 ? (
        <p className="text-xs text-gray-500">Nothing uploaded yet. Use Upload to add a file.</p>
      ) : null}
      {visible.some((item) => !item.usable) ? (
        <p className="mb-2 text-xs text-gray-500">
          Greyed-out files (PNG, WebP, etc.) can't be posted to Instagram. Upload them again with the Upload button
          to convert them to JPEG.
        </p>
      ) : null}
      <div className="grid max-h-64 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
        {visible.map((item) => (
          <button
            key={item.key}
            type="button"
            disabled={!item.usable}
            onClick={() => onSelect(item)}
            title={
              item.usable
                ? item.uploadedAt
                  ? new Date(item.uploadedAt).toLocaleString()
                  : item.key
                : 'Instagram only accepts JPEG images and MP4/MOV videos. Upload this file again with the Upload button to convert it.'
            }
            className={`relative aspect-square overflow-hidden rounded-md border border-gray-100 bg-gray-50 ${
              item.usable ? 'hover:ring-2 hover:ring-blue-500' : 'cursor-not-allowed opacity-40'
            }`}
          >
            {item.kind === 'VIDEO' ? (
              <video src={item.url} muted preload="metadata" className="h-full w-full object-cover" />
            ) : (
              <img src={item.url} alt="" loading="lazy" className="h-full w-full object-cover" />
            )}
            {item.kind === 'VIDEO' || !item.usable ? (
              <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 text-[10px] font-medium uppercase text-white">
                {item.usable ? 'Video' : (item.key.split('?')[0].split('.').pop() || 'file')}
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}

const PUBLISH_TYPES: { value: InstagramPublishType; label: string; hint: string }[] = [
  { value: 'IMAGE', label: 'Image', hint: 'Single photo post. JPEG only, public URL.' },
  { value: 'REELS', label: 'Reel', hint: 'MP4/MOV video, 3s–15min, 9:16 recommended. Processing can take a few minutes.' },
  { value: 'CAROUSEL', label: 'Carousel', hint: '2–10 images and/or videos in one swipeable post.' },
  { value: 'STORIES', label: 'Story', hint: 'Image or video shown for 24 hours. Captions are not supported on stories.' },
];

const inputClass = 'mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm';

/** URL input with an Upload button: picks a local file, uploads it, and fills in the link. */
function MediaUrlField({
  value,
  onChange,
  accept,
  placeholder,
  disabled,
  onUploaded,
}: {
  value: string;
  onChange: (url: string) => void;
  accept: string;
  placeholder?: string;
  disabled?: boolean;
  onUploaded?: (kind: 'IMAGE' | 'VIDEO') => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);

  const allowImages = accept.includes('image');
  const allowVideos = accept.includes('video');

  const pick = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    setUploadError('');
    const result = await uploadInstagramMedia(file);
    setUploading(false);
    if (!result.success || !result.url) {
      setUploadError(result.error || 'Upload failed.');
      return;
    }
    onChange(result.url);
    if (result.kind) onUploaded?.(result.kind);
  };

  return (
    <div className="mt-1">
      <div className="flex gap-2">
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder || 'Paste a public URL or upload a file'}
          disabled={disabled || uploading}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-normal"
        />
        <label
          className={`flex shrink-0 cursor-pointer items-center rounded-lg border border-gray-200 px-3 text-sm font-medium text-blue-600 hover:bg-gray-50 ${
            disabled || uploading ? 'pointer-events-none opacity-50' : ''
          }`}
        >
          {uploading ? 'Uploading...' : 'Upload'}
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={(event) => {
              pick(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </label>
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => setLibraryOpen((open) => !open)}
          className="shrink-0 rounded-lg border border-gray-200 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Library
        </button>
      </div>
      {uploadError ? <p className="mt-1 text-xs font-normal text-red-600">{uploadError}</p> : null}
      {libraryOpen ? (
        <MediaLibraryPicker
          kinds={[...(allowImages ? ['IMAGE' as const] : []), ...(allowVideos ? ['VIDEO' as const] : [])]}
          onClose={() => setLibraryOpen(false)}
          onSelect={(media) => {
            onChange(media.url);
            onUploaded?.(media.kind);
            setLibraryOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** Matches the server's check, with a little room so a slow form submit is not rejected. */
const MIN_SCHEDULE_LEAD_MS = 5 * 60 * 1000;

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/** `<input type="datetime-local">` value in the browser's time zone. */
function toLocalInput(date: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
}

function atTime(daysFromToday: number, hours: number) {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  date.setHours(hours, 0, 0, 0);
  return date;
}

function quickTimes(): { label: string; at: Date }[] {
  const inAnHour = new Date(Date.now() + 60 * 60 * 1000);
  inAnHour.setMinutes(Math.ceil(inAnHour.getMinutes() / 5) * 5, 0, 0);
  const options = [{ label: 'In 1 hour', at: inAnHour }];
  if (new Date().getHours() < 17) options.push({ label: 'This evening, 6 PM', at: atTime(0, 18) });
  options.push({ label: 'Tomorrow, 10 AM', at: atTime(1, 10) });
  options.push({ label: 'Tomorrow, 6 PM', at: atTime(1, 18) });
  return options;
}

function formatWhen(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function relativeWhen(iso: string) {
  const diff = new Date(iso).getTime() - Date.now();
  if (Math.abs(diff) < 60 * 1000) return 'now';
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  const minutes = Math.round(diff / 60000);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 48) return rtf.format(hours, 'hour');
  return rtf.format(Math.round(hours / 24), 'day');
}

function WhenPicker({
  value,
  onChange,
  error,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  error: string;
  disabled?: boolean;
}) {
  const options = quickTimes();
  const chosen = value ? new Date(value) : null;
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="mb-2 text-sm font-medium text-gray-700">When should it go live?</p>
      <div className="mb-2 flex flex-wrap gap-2">
        {options.map((option) => {
          const optionValue = toLocalInput(option.at);
          return (
            <button
              key={option.label}
              type="button"
              disabled={disabled}
              onClick={() => onChange(optionValue)}
              className={`rounded-full border px-3 py-1 text-sm ${
                value === optionValue
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <input
        type="datetime-local"
        value={value}
        min={toLocalInput(new Date(Date.now() + MIN_SCHEDULE_LEAD_MS))}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm sm:w-auto"
      />
      {error ? (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      ) : chosen ? (
        <p className="mt-2 text-sm text-gray-600">
          Posts {formatWhen(chosen.toISOString())} · {relativeWhen(chosen.toISOString())}
        </p>
      ) : null}
    </div>
  );
}

/** Loads the account's scheduled posts and keeps them fresh while the screen is open. */
function useScheduledPosts(businessIgId: string, onPublished: () => void) {
  const [posts, setPosts] = useState<InstagramScheduledPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const pendingIds = useRef<Set<string>>(new Set());
  const onPublishedRef = useRef(onPublished);
  onPublishedRef.current = onPublished;

  useEffect(() => {
    if (!businessIgId) return;
    let cancelled = false;
    getScheduledInstagramPosts(businessIgId)
      .then((result) => {
        if (cancelled) return;
        if (!result.success) {
          setError(result.error || 'Could not load scheduled posts.');
          return;
        }
        setError(null);
        // A post we were waiting on just went live: refresh the Instagram posts list.
        const justPublished = result.posts.some(
          (post) => post.status === 'published' && pendingIds.current.has(post.id)
        );
        pendingIds.current = new Set(
          result.posts.filter((post) => post.status === 'scheduled' || post.status === 'processing').map((post) => post.id)
        );
        setPosts(result.posts);
        if (justPublished) onPublishedRef.current();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [businessIgId, reloadKey]);

  // Check often while something is posting or due soon, otherwise once a minute.
  useEffect(() => {
    const soon = Date.now() + 2 * 60 * 1000;
    const busy = posts.some(
      (post) =>
        post.posting ||
        post.status === 'processing' ||
        (post.status === 'scheduled' && new Date(post.scheduledAt).getTime() < soon)
    );
    const timer = setTimeout(() => setReloadKey((key) => key + 1), busy ? 10000 : 60000);
    return () => clearTimeout(timer);
  }, [posts]);

  const upcoming = posts
    .filter((post) => post.status === 'scheduled' || post.status === 'processing')
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
  const failed = posts
    .filter((post) => post.status === 'failed')
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  return { upcoming, failed, loading, error, reload: () => setReloadKey((key) => key + 1) };
}

function previewOf(request: InstagramPublishRequest): { url: string; video: boolean } | null {
  const type = request.mediaType || 'IMAGE';
  if (type === 'CAROUSEL') {
    const first = request.items?.find((item) => item.url);
    return first ? { url: first.url, video: first.type === 'VIDEO' } : null;
  }
  if (type === 'REELS') {
    if (request.coverUrl) return { url: request.coverUrl, video: false };
    return request.videoUrl ? { url: request.videoUrl, video: true } : null;
  }
  if (request.imageUrl) return { url: request.imageUrl, video: false };
  return request.videoUrl ? { url: request.videoUrl, video: true } : null;
}

function ScheduledList({
  posts,
  kind,
  loading,
  error,
  businessIgId,
  editingId,
  onEdit,
  onChanged,
}: {
  posts: InstagramScheduledPost[];
  kind: 'scheduled' | 'failed';
  loading: boolean;
  error: string | null;
  businessIgId: string;
  editingId: string | null;
  onEdit: (post: InstagramScheduledPost) => void;
  onChanged: () => void;
}) {
  if (loading && !posts.length) return <p className="text-sm text-gray-500">Loading...</p>;
  if (error && !posts.length) return <p className="text-sm text-red-600">{error}</p>;
  if (!posts.length) {
    return (
      <p className="text-sm text-gray-500">
        {kind === 'failed'
          ? 'No failed posts.'
          : 'Nothing scheduled. Use the ▾ next to the Publish button and choose Schedule….'}
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {posts.map((post) => (
        <ScheduledCard
          key={post.id}
          post={post}
          businessIgId={businessIgId}
          editing={editingId === post.id}
          onEdit={() => onEdit(post)}
          onChanged={onChanged}
        />
      ))}
    </div>
  );
}

function ScheduledCard({
  post,
  businessIgId,
  editing,
  onEdit,
  onChanged,
}: {
  post: InstagramScheduledPost;
  businessIgId: string;
  editing: boolean;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const preview = previewOf(post.request);
  const typeLabel = PUBLISH_TYPES.find((type) => type.value === (post.request.mediaType || 'IMAGE'))?.label || 'Post';
  const caption = (post.request.caption || '').split('\n')[0].trim();
  const posting = post.posting || post.status === 'processing';
  const failed = post.status === 'failed';

  const run = async (action: () => Promise<{ success?: boolean; error?: string }>) => {
    setBusy(true);
    setActionError('');
    try {
      const result = await action();
      if (!result.success) setActionError(result.error || 'Something went wrong. Try again.');
      else onChanged();
    } finally {
      setBusy(false);
      setConfirmCancel(false);
    }
  };

  const postNow = () =>
    run(() => updateScheduledInstagramPost(post.id, { businessIgId: businessIgId || undefined, postNow: true }));
  const cancel = () => run(() => cancelScheduledInstagramPost(post.id, businessIgId || undefined));

  const pill = posting
    ? { text: 'POSTING…', className: 'bg-amber-100 text-amber-800' }
    : failed
      ? { text: 'FAILED', className: 'bg-red-100 text-red-700' }
      : { text: 'SCHEDULED', className: 'bg-blue-100 text-blue-700' };

  return (
    <div
      className={`flex gap-3 rounded-xl border p-3 ${
        editing ? 'border-blue-400 bg-blue-50/40' : failed ? 'border-red-200' : 'border-gray-100'
      }`}
    >
      <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
        {preview ? (
          preview.video ? (
            <video src={preview.url} muted preload="metadata" className="h-full w-full object-cover" />
          ) : (
            <img src={preview.url} alt="" className="h-full w-full object-cover" />
          )
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${pill.className}`}>{pill.text}</span>
          <span className="text-xs text-gray-500">{typeLabel}</span>
        </div>
        <p className="mt-1 truncate text-sm font-medium text-gray-900">{caption || `${typeLabel} without a caption`}</p>
        <p className="text-xs text-gray-500">
          {posting
            ? 'Publishing now. Videos can take a few minutes.'
            : failed
              ? `Was due ${formatWhen(post.scheduledAt)}`
              : `${formatWhen(post.scheduledAt)} · ${relativeWhen(post.scheduledAt)}`}
        </p>
        {failed && post.error ? <p className="mt-1 text-sm text-red-600">{post.error}</p> : null}
        {actionError ? <p className="mt-1 text-sm text-red-600">{actionError}</p> : null}

        {posting || editing ? null : confirmCancel ? (
          <div className="mt-2 flex items-center gap-3 text-sm">
            <span className="text-gray-700">{failed ? 'Delete this post?' : 'Cancel this post?'}</span>
            <button type="button" disabled={busy} onClick={cancel} className="font-medium text-red-600">
              {busy ? 'Removing...' : `Yes, ${failed ? 'delete' : 'cancel'}`}
            </button>
            <button type="button" disabled={busy} onClick={() => setConfirmCancel(false)} className="text-gray-500">
              Keep it
            </button>
          </div>
        ) : (
          <div className="mt-2 flex flex-wrap gap-3 text-sm">
            <button type="button" disabled={busy} onClick={postNow} className="font-medium text-blue-600">
              {busy ? 'Working...' : failed ? 'Retry now' : 'Post now'}
            </button>
            <button type="button" disabled={busy} onClick={onEdit} className="font-medium text-gray-700">
              Edit
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmCancel(true)}
              className="font-medium text-gray-500"
            >
              {failed ? 'Delete' : 'Cancel'}
            </button>
          </div>
        )}
        {editing ? <p className="mt-2 text-xs text-blue-700">Editing above…</p> : null}
      </div>
    </div>
  );
}

function PublishScreen({ businessIgId }: { businessIgId: string }) {
  const [mediaType, setMediaType] = useState<InstagramPublishType>('IMAGE');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [storyKind, setStoryKind] = useState<'IMAGE' | 'VIDEO'>('IMAGE');
  const [items, setItems] = useState<InstagramCarouselItem[]>([
    { type: 'IMAGE', url: '' },
    { type: 'IMAGE', url: '' },
  ]);
  const [caption, setCaption] = useState('');
  const [altText, setAltText] = useState('');
  const [collaborators, setCollaborators] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [thumbOffset, setThumbOffset] = useState('');
  const [shareToFeed, setShareToFeed] = useState(true);
  const [audioName, setAudioName] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [mediaId, setMediaId] = useState('');
  const [postsRefresh, setPostsRefresh] = useState(0);
  const [mode, setMode] = useState<'now' | 'schedule'>('now');
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  const [when, setWhen] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [listTab, setListTab] = useState<'posts' | 'scheduled' | 'failed'>('posts');
  const scheduled = useScheduledPosts(businessIgId, () => setPostsRefresh((value) => value + 1));

  const whenDate = when ? new Date(when) : null;
  const whenError =
    mode !== 'schedule'
      ? ''
      : !whenDate || Number.isNaN(whenDate.getTime())
        ? 'Pick a date and time.'
        : whenDate.getTime() < Date.now() + MIN_SCHEDULE_LEAD_MS
          ? 'Pick a time at least 5 minutes from now.'
          : '';

  const filledItems = items.filter((item) => item.url.trim());
  const canPublish =
    mediaType === 'IMAGE'
      ? Boolean(imageUrl.trim())
      : mediaType === 'REELS'
        ? Boolean(videoUrl.trim())
        : mediaType === 'CAROUSEL'
          ? filledItems.length >= 2 && filledItems.length <= 10
          : Boolean((storyKind === 'VIDEO' ? videoUrl : imageUrl).trim());

  const buildRequest = (): InstagramPublishRequest => {
    const collaboratorList = collaborators
      .split(',')
      .map((name) => name.trim().replace(/^@/, ''))
      .filter(Boolean);
    const shared = {
      mediaType,
      businessIgId: businessIgId || undefined,
      caption: mediaType === 'STORIES' ? undefined : caption.trim() || undefined,
      collaborators: mediaType === 'STORIES' || !collaboratorList.length ? undefined : collaboratorList,
    };
    if (mediaType === 'IMAGE') {
      return { ...shared, imageUrl: imageUrl.trim(), altText: altText.trim() || undefined };
    }
    if (mediaType === 'REELS') {
      const offset = Number(thumbOffset);
      return {
        ...shared,
        videoUrl: videoUrl.trim(),
        coverUrl: coverUrl.trim() || undefined,
        thumbOffset: thumbOffset.trim() && Number.isFinite(offset) ? offset : undefined,
        shareToFeed,
        audioName: audioName.trim() || undefined,
      };
    }
    if (mediaType === 'CAROUSEL') {
      return { ...shared, items: filledItems.map((item) => ({ type: item.type, url: item.url.trim() })) };
    }
    return storyKind === 'VIDEO'
      ? { ...shared, videoUrl: videoUrl.trim() }
      : { ...shared, imageUrl: imageUrl.trim() };
  };

  const resetForm = () => {
    setImageUrl('');
    setVideoUrl('');
    setItems([
      { type: 'IMAGE', url: '' },
      { type: 'IMAGE', url: '' },
    ]);
    setCaption('');
    setAltText('');
    setCollaborators('');
    setCoverUrl('');
    setThumbOffset('');
    setShareToFeed(true);
    setAudioName('');
  };

  /** Puts a scheduled post back into the form for editing. */
  const fillForm = (request: InstagramPublishRequest) => {
    resetForm();
    const type = request.mediaType || 'IMAGE';
    setMediaType(type);
    setImageUrl(request.imageUrl || '');
    setVideoUrl(request.videoUrl || '');
    if (type === 'STORIES') setStoryKind(request.videoUrl ? 'VIDEO' : 'IMAGE');
    if (type === 'CAROUSEL' && request.items?.length) {
      setItems(request.items.map((item) => ({ type: item.type || 'IMAGE', url: item.url || '' })));
    }
    setCaption(request.caption || '');
    setAltText(request.altText || '');
    setCollaborators((request.collaborators || []).map((name) => `@${name}`).join(', '));
    setCoverUrl(request.coverUrl || '');
    setThumbOffset(typeof request.thumbOffset === 'number' ? String(request.thumbOffset) : '');
    setShareToFeed(request.shareToFeed !== false);
    setAudioName(request.audioName || '');
  };

  const startEdit = (post: InstagramScheduledPost) => {
    fillForm(post.request);
    setEditingId(post.id);
    setMode('schedule');
    const at = new Date(post.scheduledAt);
    // A failed post's old time is in the past, so suggest a fresh one.
    setWhen(toLocalInput(at.getTime() > Date.now() + MIN_SCHEDULE_LEAD_MS ? at : quickTimes()[0].at));
    setError(null);
    setNotice('');
    setMediaId('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stopEdit = () => {
    setEditingId(null);
    setWhen('');
    setMode('now');
    resetForm();
  };

  const schedule = async () => {
    if (!canPublish || whenError || !whenDate) return;
    setSending(true);
    setError(null);
    setNotice('');
    setMediaId('');
    const payload = {
      businessIgId: businessIgId || undefined,
      scheduledAt: whenDate.toISOString(),
      timeZone: browserTimeZone(),
      request: { ...buildRequest(), businessIgId: undefined as string | undefined },
    };
    try {
      const result = editingId
        ? await updateScheduledInstagramPost(editingId, payload)
        : await scheduleInstagramPost(payload);
      if (!result.success) {
        setError(result.error || 'Could not schedule this post.');
        return;
      }
      setNotice(`${editingId ? 'Updated' : 'Scheduled'} for ${formatWhen(payload.scheduledAt)}.`);
      setEditingId(null);
      setWhen('');
      resetForm();
      setListTab('scheduled');
      scheduled.reload();
    } finally {
      setSending(false);
    }
  };

  const publish = async () => {
    if (!canPublish) return;
    if (mode === 'schedule') {
      await schedule();
      return;
    }
    setSending(true);
    setNotice('');
    setError(null);
    setMediaId('');
    setProgress('');
    try {
      const result = await publishInstagramPost(buildRequest(), (status) =>
        setProgress(`Instagram is processing the media (${status})...`)
      );
      if (!result?.success) {
        setError(result?.error || 'Could not publish this post.');
        return;
      }
      setMediaId(result.mediaId || '');
      resetForm();
      setPostsRefresh((value) => value + 1);
    } catch {
      setError('Could not publish this post.');
    } finally {
      setSending(false);
      setProgress('');
    }
  };

  const updateItem = (index: number, patch: Partial<InstagramCarouselItem>) =>
    setItems((list) => list.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const activeType = PUBLISH_TYPES.find((type) => type.value === mediaType);

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 max-w-2xl">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Publish to Instagram</h2>
      <p className="mb-4 text-sm text-gray-500">
        Upload a file or paste a public URL. The post goes to the connected Instagram account.
      </p>
      {scheduled.failed.length ? (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>
            {scheduled.failed.length === 1
              ? '1 scheduled post could not be published.'
              : `${scheduled.failed.length} scheduled posts could not be published.`}
          </span>
          <button
            type="button"
            onClick={() => {
              setListTab('failed');
              document.getElementById('instagram-post-lists')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="font-medium underline"
          >
            Review
          </button>
        </div>
      ) : null}
      {editingId ? (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
          <span>Editing a scheduled post.</span>
          <button type="button" onClick={stopEdit} disabled={sending} className="font-medium underline">
            Stop editing
          </button>
        </div>
      ) : null}
      <div className="mb-4 inline-flex rounded-lg border border-gray-200 p-1">
        {PUBLISH_TYPES.map((type) => (
          <button
            key={type.value}
            type="button"
            disabled={sending}
            onClick={() => {
              setMediaType(type.value);
              setError(null);
              setMediaId('');
            }}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              mediaType === type.value ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>
      {activeType ? <p className="mb-4 text-xs text-gray-500">{activeType.hint}</p> : null}
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          publish();
        }}
      >
        {mediaType === 'STORIES' ? (
          <div className="flex gap-4 text-sm text-gray-700">
            {(['IMAGE', 'VIDEO'] as const).map((kind) => (
              <label key={kind} className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={storyKind === kind}
                  onChange={() => setStoryKind(kind)}
                />
                {kind === 'IMAGE' ? 'Image story' : 'Video story'}
              </label>
            ))}
          </div>
        ) : null}

        {mediaType === 'IMAGE' || (mediaType === 'STORIES' && storyKind === 'IMAGE') ? (
          <label className="block text-sm font-medium text-gray-700">
            Image
            <MediaUrlField
              value={imageUrl}
              onChange={setImageUrl}
              accept="image/*"
              disabled={sending}
            />
          </label>
        ) : null}

        {mediaType === 'REELS' || (mediaType === 'STORIES' && storyKind === 'VIDEO') ? (
          <label className="block text-sm font-medium text-gray-700">
            Video
            <MediaUrlField
              value={videoUrl}
              onChange={setVideoUrl}
              accept="video/mp4,video/quicktime"
              disabled={sending}
            />
          </label>
        ) : null}

        {mediaType === 'CAROUSEL' ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Carousel items ({filledItems.length}/10)</p>
            {items.map((item, index) => (
              <div key={index} className="flex items-start gap-2">
                <select
                  value={item.type}
                  onChange={(event) => updateItem(index, { type: event.target.value as 'IMAGE' | 'VIDEO' })}
                  className="mt-1 rounded-lg border border-gray-200 px-2 py-2 text-sm"
                >
                  <option value="IMAGE">Image</option>
                  <option value="VIDEO">Video</option>
                </select>
                <div className="w-full">
                  <MediaUrlField
                    value={item.url}
                    onChange={(url) => updateItem(index, { url })}
                    onUploaded={(kind) => updateItem(index, { type: kind })}
                    accept="image/*,video/mp4,video/quicktime"
                    placeholder={`Item ${index + 1}: paste URL or upload`}
                    disabled={sending}
                  />
                </div>
                {items.length > 2 ? (
                  <button
                    type="button"
                    onClick={() => setItems((list) => list.filter((_, i) => i !== index))}
                    className="mt-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600"
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
            {items.length < 10 ? (
              <button
                type="button"
                onClick={() => setItems((list) => [...list, { type: 'IMAGE', url: '' }])}
                className="text-sm font-medium text-blue-600"
              >
                + Add item
              </button>
            ) : null}
          </div>
        ) : null}

        {mediaType === 'REELS' ? (
          <>
            <label className="block text-sm font-medium text-gray-700">
              Cover image (optional)
              <MediaUrlField
                value={coverUrl}
                onChange={setCoverUrl}
                accept="image/*"
                disabled={sending}
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm font-medium text-gray-700">
                Thumbnail frame (ms, optional)
                <input
                  value={thumbOffset}
                  onChange={(event) => setThumbOffset(event.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="e.g. 1500"
                  className={inputClass}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Audio name (optional)
                <input
                  value={audioName}
                  onChange={(event) => setAudioName(event.target.value)}
                  placeholder="Original audio"
                  className={inputClass}
                />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={shareToFeed}
                onChange={(event) => setShareToFeed(event.target.checked)}
              />
              Also show this reel in the main feed
            </label>
          </>
        ) : null}

        {mediaType === 'IMAGE' ? (
          <label className="block text-sm font-medium text-gray-700">
            Alt text (optional)
            <input
              value={altText}
              onChange={(event) => setAltText(event.target.value)}
              placeholder="Describe the image for screen readers"
              className={inputClass}
            />
          </label>
        ) : null}

        {mediaType !== 'STORIES' ? (
          <>
            <label className="block text-sm font-medium text-gray-700">
              Caption
              <textarea
                value={caption}
                onChange={(event) => setCaption(event.target.value)}
                rows={3}
                maxLength={2200}
                className="mt-1 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Collaborators (optional, up to 3 usernames, comma separated)
              <input
                value={collaborators}
                onChange={(event) => setCollaborators(event.target.value)}
                placeholder="@partner1, @partner2"
                className={inputClass}
              />
            </label>
          </>
        ) : null}

        {mode === 'schedule' ? (
          <WhenPicker value={when} onChange={setWhen} error={whenError} disabled={sending} />
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative inline-flex">
            <button
              type="submit"
              disabled={sending || !canPublish || Boolean(whenError)}
              className={`bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                editingId ? 'rounded-lg' : 'rounded-l-lg'
              }`}
            >
              {sending
                ? mode === 'schedule'
                  ? 'Saving...'
                  : 'Publishing...'
                : editingId
                  ? 'Save changes'
                  : `${mode === 'schedule' ? 'Schedule' : 'Publish'} ${activeType?.label.toLowerCase() || ''}`}
            </button>
            {editingId ? null : (
              <button
                type="button"
                aria-label="Publish now or schedule"
                aria-expanded={modeMenuOpen}
                disabled={sending}
                onClick={() => setModeMenuOpen((open) => !open)}
                onBlur={() => setTimeout(() => setModeMenuOpen(false), 150)}
                className="rounded-r-lg border-l border-blue-500 bg-blue-600 px-2 py-2 text-sm text-white disabled:opacity-50"
              >
                ▾
              </button>
            )}
            {modeMenuOpen ? (
              <div className="absolute left-0 top-full z-10 mt-1 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                {(
                  [
                    ['now', 'Publish now', 'Goes live right away'],
                    ['schedule', 'Schedule…', 'Pick a date and time'],
                  ] as const
                ).map(([value, label, hint]) => (
                  <button
                    key={value}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setMode(value);
                      setModeMenuOpen(false);
                      if (value === 'schedule' && !when) setWhen(toLocalInput(quickTimes()[0].at));
                    }}
                    className={`block w-full px-3 py-2 text-left hover:bg-gray-50 ${
                      mode === value ? 'bg-blue-50' : ''
                    }`}
                  >
                    <span className="block text-sm font-medium text-gray-900">{label}</span>
                    <span className="block text-xs text-gray-500">{hint}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {mode === 'schedule' && !editingId ? (
            <button
              type="button"
              disabled={sending}
              onClick={() => setMode('now')}
              className="text-sm text-gray-500 underline"
            >
              Publish now instead
            </button>
          ) : null}
        </div>
      </form>
      {progress ? <p className="mt-3 text-sm text-gray-600">{progress}</p> : null}
      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      {notice ? <p className="mt-3 text-sm text-green-700">{notice}</p> : null}
      {mediaId ? <p className="mt-3 text-sm text-green-700">Published. Media ID: {mediaId}</p> : null}
      <div id="instagram-post-lists" className="mt-8">
        <div className="mb-3 flex gap-1 border-b border-gray-200">
          {(
            [
              ['posts', 'Posts and likes', null],
              ['scheduled', 'Scheduled', scheduled.upcoming.length],
              ['failed', 'Failed', scheduled.failed.length],
            ] as const
          ).map(([value, label, count]) => (
            <button
              key={value}
              type="button"
              onClick={() => setListTab(value)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
                listTab === value
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
              {count ? (
                <span
                  className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${
                    value === 'failed' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {count}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        {listTab === 'posts' ? <PostsList businessIgId={businessIgId} refreshKey={postsRefresh} /> : null}
        {listTab !== 'posts' ? (
          <ScheduledList
            posts={listTab === 'failed' ? scheduled.failed : scheduled.upcoming}
            kind={listTab}
            loading={scheduled.loading}
            error={scheduled.error}
            businessIgId={businessIgId}
            editingId={editingId}
            onEdit={startEdit}
            onChanged={scheduled.reload}
          />
        ) : null}
      </div>
    </div>
  );
}

function InsightsScreen({ businessIgId }: { businessIgId: string }) {
  const [rows, setRows] = useState<InstagramInsight[]>([]);
  const [profile, setProfile] = useState<InstagramProfileStats | null>(null);
  const [reachLast28Days, setReachLast28Days] = useState<number | null>(null);
  const [mediaId, setMediaId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = async (nextMediaId = mediaId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getInstagramInsights({
        businessIgId: businessIgId || undefined,
        mediaId: nextMediaId.trim() || undefined,
      });
      if (!result?.success) {
        setRows([]);
        setProfile(null);
        setReachLast28Days(null);
        setError(result?.error || 'Could not load insights.');
      } else {
        setRows(result.rows);
        setProfile(result.profile);
        setReachLast28Days(result.reachLast28Days);
      }
    } catch {
      setRows([]);
      setProfile(null);
      setReachLast28Days(null);
      setError('Could not load insights.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (!businessIgId) return;
    load('');
  }, [businessIgId]);

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Instagram Insights</h2>
      <p className="mb-4 text-sm text-gray-500">
        Insights for the Instagram account assigned to this user: {businessIgId}
      </p>
      <form
        className="mb-4 flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          load();
        }}
      >
        <label className="block flex-1 text-sm font-medium text-gray-700">
          Media ID
          <input
            value={mediaId}
            onChange={(event) => setMediaId(event.target.value)}
            placeholder="Leave empty for the account"
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Fetch'}
        </button>
      </form>
      {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}
      {profile ? (
        <div className="mb-6">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">All followers and posts</h3>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="text-sm text-gray-500">Followers</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{profile.followersCount ?? '—'}</p>
            </div>
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="text-sm text-gray-500">Following</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{profile.followsCount ?? '—'}</p>
            </div>
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="text-sm text-gray-500">Posts</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{profile.mediaCount ?? '—'}</p>
            </div>
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="text-sm text-gray-500">Reach, last 28 days</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{reachLast28Days ?? '—'}</p>
            </div>
          </div>
        </div>
      ) : null}
      <h3 className="mb-3 text-sm font-semibold text-gray-900">New activity by day</h3>
      {loaded && !loading && rows.length === 0 && !profile && !error ? (
        <p className="text-sm text-gray-500">No insight numbers returned for this account.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((row) => (
            <div key={row.id || row.name} className="rounded-xl border border-gray-100 p-4">
              <p className="text-sm font-medium text-gray-900">{row.title || row.name}</p>
              <p className="mt-1 text-xs text-gray-500">
                {row.name === 'follower_count' ? 'New followers that day' : 'Unique views that day'}
              </p>
              <div className="mt-3 space-y-1">
                {(row.values || []).map((item) => (
                  <div key={item.end_time || String(item.value)} className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                      {item.end_time ? new Date(item.end_time).toLocaleDateString() : row.period}
                    </span>
                    <span className="font-semibold text-gray-900">{formatInsightValue(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
